import { PrivateURLConstants } from '@constants/private-url.constant';
import { ByCustomFieldsCount } from '@modules/dashboard/models/by-custom-field-count.model';
import { ByPriorityCount } from '@modules/dashboard/models/by-priority-count.model';
import { ByTypeCount } from '@modules/dashboard/models/by-type-count.model';
import {
  Page,
  TestCase,
  byPassSpecialCharactersInLikeOperator,
} from '@testsigma/organism';

import { useQuery } from 'react-query';
import { default as Api, default as api } from '../../../api/instance';
import { CopilotAssignTDPRequest } from '../models/copilot-assign-tdp.model';
import { TestCaseBulkUpdateRequest } from '../models/test-case-bulk-update.model';
import {
  TestCaseAlphaCreateRequest,
  TestCaseBulkAlphaCreateRequest,
  TestCaseCreateRequest,
} from '../models/test-case-create.model';
const nameSpace = PrivateURLConstants.CASE;

const filterNameSpace = nameSpace + '/filter';
const importSpace = '/import';

const findAll = (query?, size?, page?, sort?, payload?) => {
  return api
    .get(nameSpace, {
      query,
      size,
      page,
      sort,
    })
    .then((res) => new Page<TestCase>().deserialize(res, TestCase))
    .catch((e) => Promise.reject(e));
};

const filter = (
  filterId: number,
  versionId: number | string,
  query?: string,
  page?: number,
  sort?: string,
  size?: number,
) => {
  return api
    .get(filterNameSpace + '/' + filterId, {
      versionId,
      query,
      page,
      sort,
      size,
    })
    .then((res) => new Page<TestCase>().deserialize(res, TestCase))
    .catch((e) => Promise.reject(e));
};

const show = (id: number) => {
  return api
    .get(`${nameSpace}/${id}`)
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => {
      throw e;
    });
};

const getLastResultId = (id: number) => {
  return api
    .get(`${nameSpace}/${id}/last_result_id`)
    .then((res) => res)
    .catch((e) => {
      throw e;
    });
};

const create = (payload: TestCaseCreateRequest) => {
  return api.post(nameSpace + '/create_new', payload.serialize());
};

const findSelectedTestCases = (
  payload: number[],
  query?,
  page?,
  size?,
  payloadKey = 'ids',
) => {
  let completeQuery = { query: query, page, size };

  // Handling it here becaus we don't do this for put methord
  if (completeQuery?.query) {
    if (!completeQuery?.query?.query && completeQuery?.query?.includes(':*')) {
      completeQuery.query = byPassSpecialCharactersInLikeOperator(
        completeQuery?.query,
      );
    } else if (completeQuery?.query?.query?.includes(':*')) {
      completeQuery.query.query = byPassSpecialCharactersInLikeOperator(
        completeQuery?.query?.query,
      );
    }
  }
  return api
    .put(
      nameSpace + '/selected_test_cases',
      { [payloadKey]: payload },
      completeQuery,
    )
    .then((res) => new Page<TestCase>().deserialize(res, TestCase))
    .catch((e) => Promise.reject(e));
};

const findAvailableTestCases = (
  query?,
  size?,
  page?,
  sort?,
  payload?: number[],
) => {
  if (query && query?.includes(':*')) {
    query = byPassSpecialCharactersInLikeOperator(query);
  }
  const completeQuery = { query, page, size };
  return api
    .put(nameSpace + '/available_test_cases', { ids: payload }, completeQuery)
    .then((res) => new Page<TestCase>().deserialize(res, TestCase))
    .catch((e) => Promise.reject(e));
};

const update = (payload: TestCase) => {
  return api
    .put<TestCase>(nameSpace + `/${payload.id}`, payload.serialize())
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const copy = (copyRequest: {
  name: string;
  stepIds?: number[];
  testCaseId: number;
  isStepGroup?: boolean;
  isReplace: boolean;
  isTestComponent?: boolean;
}) => {
  return api
    .post<TestCase>(nameSpace + '/copy', copyRequest)
    .then((res) => new TestCase().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const isStepGroupEligible = (testcaseId: number) => {
  return api
    .get(
      `${nameSpace}/stepgroup/creationEligible?testCaseId=${testcaseId}&stepGroupId=${testcaseId}&isChild=false`,
    )
    .then(
      (res) => {
        return res;
      },
      (error) => new Error(error),
    )
    .catch((error) => Promise.reject(error));
};

const moveToTrash = (ids: number[], applicationVersionId: number) => {
  return api
    .remove(
      `${nameSpace}/mark_as_delete?applicationVersionId=${applicationVersionId}`,
      { ids: ids.toString() },
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const markAsDelete = (id: number, applicationVersionId: number) => {
  return api
    .remove(
      `${nameSpace}/${id}/mark_as_delete?applicationVersionId=${applicationVersionId}`,
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const bulkUpdate = (
  payload: TestCaseBulkUpdateRequest,
  applicationVersionId: number | string,
) => {
  return api
    .put(
      `${nameSpace}/bulk_update?applicationVersionId=${applicationVersionId}`,
      payload.serialize(),
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const restore = (id: number, applicationVersionId: number) => {
  return api
    .put(
      `${nameSpace}/${id}/restore?applicationVersionId=${applicationVersionId}`,
      {},
    )
    .then((res) => res)
    .catch((e) => {
      throw e;
    });
};

const destroy = (id: number, applicationVersionId: number) => {
  return api
    .remove(`${nameSpace}/${id}?applicationVersionId=${applicationVersionId}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const importData = (file: File, filterQuery: Record<string, any>) => {
  const formData: FormData = new FormData();
  formData.append('file', file);
  return api
    .put(
      `${nameSpace}${importSpace}`,
      formData,
      filterQuery,
      'multipart/form-data',
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const findTestcasesHavingForLoopTDP = (tdpId: number) => {
  return api
    .get(`${nameSpace}/test_data_profile/${tdpId}`)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};
const byStatusCount = (versionId: number) => {
  return api
    .get(nameSpace + '/break_up_by_status?versionId=' + versionId)
    .then((res) => {
      return res;
    })
    .catch((e) => {
      return e;
    });
};
const byTypeCount = (versionId: number) => {
  return api
    .get(nameSpace + '/break_up_by_type?versionId=' + versionId)
    .then((res: any) => {
      const result: ByTypeCount[] = [];
      res.forEach((element) => {
        result.push(new ByTypeCount().deserialize(element));
      });
      return result;
    })
    .catch((e) => {
      return e;
    });
};
const byPriorityCount = (versionId: number) => {
  return api
    .get(nameSpace + '/break_up_by_priority?versionId=' + versionId)
    .then((res: any) => {
      const result: ByPriorityCount[] = [];
      res.forEach((element) => {
        result.push(new ByPriorityCount().deserialize(element));
      });
      return result;
    })
    .catch((e) => {
      return e;
    });
};

const byCustomField = (versionId: number, customFieldName: string) => {
  return api
    .get(
      nameSpace +
        '/break_up_by_custom_fields?versionId=' +
        versionId +
        '&customFieldName=' +
        customFieldName,
    )
    .then((res: any) => {
      const result: ByCustomFieldsCount[] = [];
      res.forEach((element) => {
        result.push(new ByCustomFieldsCount().deserialize(element));
      });
      return result;
    })
    .catch((e) => {
      throw e;
    });
};

const getMissedParameters = (
  testCaseId?: number,
  currentTestDataProfileId?: number,
) => {
  return api
    .get(`${nameSpace}/validate_tdp_change`, {
      testCaseId,
      currentTestDataProfileId,
    })
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};
const fetchTotalErrorCount = (testcaseId: number) => {
  return api
    .get(nameSpace + `/${testcaseId}/error_count`)
    .then((res) => res)
    .catch((e) => {
      throw e;
    });
};

const createAlphaTestCase = (payload: TestCaseAlphaCreateRequest) => {
  return new Promise((resolve, reject) => {
    api
      .post(nameSpace + '/create_alpha_test_case', payload)
      .then((res: any) => resolve(new TestCase().deserialize(res)))
      .catch((e) => reject(e));
  });
};

const bulkCreateAlphaTestCase = (
  payload: TestCaseBulkAlphaCreateRequest,
  applicationVersionId: number,
): Promise<TestCase[]> => {
  return new Promise((resolve, reject) => {
    api
      .post(
        `${nameSpace}/bulk_create_alpha_test_cases?applicationVersionId:${applicationVersionId}`,
        payload,
      )
      .then((res: any) => {
        if (Array.isArray(res) && res?.length > 0) {
          const deserializedItems = res.map((item) =>
            new TestCase().deserialize(item),
          );
          resolve(deserializedItems);
        } else {
          resolve([]);
        }
      })
      .catch((e) => reject(e));
  });
};

export const useFetchTestCaseById = ({
  testCaseId,
  enabled,
}: {
  testCaseId: number;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: ['testcase', testCaseId],
    queryFn: async () => {
      const response = await show(testCaseId);
      return new TestCase().deserialize(response);
    },
    enabled,
  });
};

const assignCopilotTDP = (payload: CopilotAssignTDPRequest) => {
  return api
    .post(`${nameSpace}/assign-copilot-tdp`, payload)
    .then((res: any) => res)
    .catch((e) => Promise.reject(e));
};

const updatedById = (testCaseId: number, payload: any) =>
  Api.patch(`${nameSpace}/${testCaseId}`, payload)
    .then((res) => new TestCase().deserialize(res))
    .catch((err) => Promise.reject(err));

const isAllTestStepsDisabled = (testCaseId: number) =>
  api
    .get(`${nameSpace}/validate/${testCaseId}`)
    .then((res: any) => res)
    .catch((e) => {
      throw e;
    });

const TestCaseService = {
  findAll,
  show,
  getLastResultId,
  create,
  update,
  copy,
  isStepGroupEligible,
  moveToTrash,
  markAsDelete,
  bulkUpdate,
  importData,
  destroy,
  restore,
  filter,
  findTestcasesHavingForLoopTDP,
  byStatusCount,
  byTypeCount,
  byPriorityCount,
  byCustomField,
  findSelectedTestCases,
  findAvailableTestCases,
  fetchTotalErrorCount,
  getMissedParameters,
  createAlphaTestCase,
  bulkCreateAlphaTestCase,
  updatedById,
  assignCopilotTDP,
  isAllTestStepsDisabled,
};

export default TestCaseService;
